import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SrModelVersionEnum } from '@enums/sr-model-version.enum';

/**
 * Decides whether a legacy (V1) or the new (V2) UI should open a service request,
 * based on the `modelVersion` value returned by the backend, and performs the redirect.
 *
 * Used during the V1 -> V2 transition so that V2 service requests entered through a
 * legacy flow (list, open/edit, create) are bounced into the V2 pages.
 */
@Injectable({ providedIn: 'root' })
export class SrRedirectService {
  private readonly router = inject(Router);

  /** True when the service request belongs to the V2 model. Missing/undefined is treated as legacy. */
  isV2(modelVersion: number | null | undefined): boolean {
    return modelVersion === SrModelVersionEnum.V2;
  }

  /**
   * Builds the itinerary route path for a service request in the correct UI version.
   * @param serviceRequestId service request id
   * @param modelVersion backend `modelVersion` (1 = legacy, 2 = V2)
   * @param v1Suffix optional legacy path suffix (e.g. 'csr' used right after creation)
   */
  private getServiceRequestPath(serviceRequestId: string, modelVersion: number | null | undefined, v1Suffix?: string): string {
    if (this.isV2(modelVersion)) {
      return `/v2/itinerary/${serviceRequestId}`;
    }

    return v1Suffix ? `/itinerary/${serviceRequestId}/${v1Suffix}` : `/itinerary/${serviceRequestId}`;
  }

  /** Opens an existing service request in the correct UI version. */
  openServiceRequest(serviceRequestId: string, modelVersion: number | null | undefined, v1Suffix?: string): Promise<boolean> {
    return this.router.navigateByUrl(this.getServiceRequestPath(serviceRequestId, modelVersion, v1Suffix));
  }
}
